import {
  Typography,
  TypographyVariants,
  TypographyColors,
  Badge,
  BadgeSizes,
} from '@carboledger/ui';
import { isValidElement } from 'react';
import styles from './FieldSection.module.scss';

/**
 * Props:
 *   label  the field name
 *   value  text, or a node (e.g. a Badge) to render in its place
 *   badge  optional { label, variant } rendered beside a text value
 */
const FieldEntry = ({ label, value, badge }) => (
  <div className={styles.field}>
    <Typography variant={TypographyVariants.caption} color={TypographyColors.placeholder}>
      {label}
    </Typography>
    {isValidElement(value) ? (
      value
    ) : (
      <div className={styles.valueRow}>
        <Typography variant={TypographyVariants.bodySm} color={TypographyColors.primary}>
          {value}
        </Typography>
        {badge && (
          <Badge variant={badge.variant} size={BadgeSizes.sm}>
            {badge.label}
          </Badge>
        )}
      </div>
    )}
  </div>
);

/**
 * Props:
 *   heading  optional section title (rendered uppercase via CSS) — omit when the caller
 *            draws its own heading above the section
 *   fields   Array<{ id?, label, value, badge?: { label, variant } }> — `label` and `value` may
 *            each be a node (e.g. a Badge); `badge` renders beside the value; `id` keys a row
 *            whose label is a node or repeats
 *   footer   node closing the column, e.g. the alert that says why its values are dashes
 */
export const FieldSection = ({ heading, fields, footer = null }) => (
  <div className={styles.section}>
    {heading && (
      <div className={styles.sectionHeader}>
        <Typography variant={TypographyVariants.caption} className={styles.sectionHeading}>
          {heading}
        </Typography>
        <span className={styles.sectionRule} />
      </div>
    )}
    {fields.map((entry) =>
      Array.isArray(entry) ? (
        <div key={entry.map((field) => field.id ?? field.label).join('|')} className={styles.row}>
          {entry.map((field) => (
            <FieldEntry
              key={field.id ?? field.label}
              label={field.label}
              value={field.value}
              badge={field.badge}
            />
          ))}
        </div>
      ) : (
        <FieldEntry
          key={entry.id ?? entry.label}
          label={entry.label}
          value={entry.value}
          badge={entry.badge}
        />
      ),
    )}
    {footer}
  </div>
);
